import { Breadcrumbs } from '@/components/Breadcrumbs/Breadcrumbs';
import { Container } from '@/components/Container/Container';
import { PublicPage } from '@/components/Page/PublicPage';
import SkeletonLoader from '@/components/SkeletonLoader/SkeletonLoader';
import { CONSTANTS } from '@/constants';
import { useProcessState } from '@/hooks/useProcessState/useProcessState';
import { ESize } from '@/models/enums';
import { IDirectory, useSearchDirectories } from '@netfront/ekardo-content-library';
import { Button } from '@netfront/ui-library';
import { NextPage } from 'next';
import { useEffect, useState } from 'react';

const GetHelpAndSupportNextPage: NextPage = () => {
  const { start, complete, fail, isCompleted, isLoading } = useProcessState();

  const [directories, setDirectories] = useState<IDirectory[]>([]);

  const { handleSearchDirectories } = useSearchDirectories({
    onCompleted: ({ directories }) => {
      setDirectories(directories);
      complete();
    },
    onError: () => {
      fail();
    },
  });

  useEffect(() => {
    start();

    handleSearchDirectories({
      directoryTypeId: CONSTANTS.DIRECTORY.HELP_AND_SUPPORT.ID,
      projectId: String(process.env.REACT_APP_PROJECT_ID),
    });
  }, []);

  return (
    <PublicPage
      meta={{
        seoDescription: 'Access help and support services.',
        seoTitle: 'Get help & support',
      }}
      title="Get help & support"
    >
      <Container additionalClassNames="py-12" size={ESize.MD}>
        <div className="mb-2">
          <Breadcrumbs items={[{ content: <span>Get help & support</span>, key: 'get-help-and-support' }]} />
        </div>

        <h1 className="color-black text-uppercase">Get help & support</h1>

        <section className="py-8">
          {isLoading && (
            <ul className="flex flex-col w-full list-style-none">
              <li className="w-full py-2 m-0">
                <SkeletonLoader additionalClassNames="h-40" />
              </li>
              <li className="w-full py-2 m-0">
                <SkeletonLoader additionalClassNames="h-40" />
              </li>
              <li className="w-full py-2 m-0">
                <SkeletonLoader additionalClassNames="h-40" />
              </li>
              <li className="w-full py-2 m-0">
                <SkeletonLoader additionalClassNames="h-40" />
              </li>
            </ul>
          )}

          {isCompleted && directories.length > 0 && (
            <ul className="list-style-none m-0 flex flex-col gap-4">
              {directories.map(({ asset, description, phoneNumber, id, url, title }) => (
                <li key={id} className="m-0">
                  <div className="flex flex-col lg:flex-row items-stretch gap-12 shadow rounded-lg p-6">
                    <div className="w-full lg:w-1/5 rounded-lg bg-grey-300 p-8 flex justify-center items-center">
                      <img className="w-full h-24" src={asset ? asset.presignedUrl : '/images/logo-icon.svg'} alt={asset?.alt || ''} />
                    </div>

                    <div className="flex flex-col justify-center flex-1">
                      <h2 className="mb-4">{title}</h2>
                      <p className="mb-6">{description}</p>

                      <ul aria-label={`${title} access`} className="list-style-none m-0 flex items-center gap-3">
                        {phoneNumber && (
                          <li className="m-0">
                            <Button linkButton={{ id: title, url: `tel:${phoneNumber}` }} text={phoneNumber} size="small" />
                          </li>
                        )}
                        {url && (
                          <li className="m-0">
                            <Button linkButton={{ id: title, url, target: '_blank' }} variant="accent" text="Visit website" size="small" />
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </Container>

      <section className="bg-primary color-white py-16">
        <Container>
          <h3 className="h4 mb-6">If you don't feel like you can call any of the above services, you can also:</h3>
          <ul>
            <li>talk to someone you trust</li>
            <li>contact your GP, a social worker, psychologist or psychiatrist</li>
            <li>visit a hospital emergency department</li>
          </ul>
        </Container>
      </section>
    </PublicPage>
  );
};

export default GetHelpAndSupportNextPage;
